import { SeccionGeneracionConIa } from "@/components/plantillas/GeneracionConIa";
import {
  CabeceraDelCajon,
  CampoDelCajon,
  UsoPorPlantilla,
} from "@/components/plantillas/PiezasDelCajon";
import { BarraDePresencia, RotuloDeSeccion } from "@/components/plantillas/etiquetas";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import type { EdicionDeItemDelMaestro } from "@/lib/borradorDelMaestro";
import { INFORMES_ANALIZADOS, TIPOS_DE_CONTENIDO, type TipoDeContenido } from "@/lib/plantillas";

interface DrawerProps {
  edicion: EdicionDeItemDelMaestro | null;
  /** Datos de solo lectura del ítem guardado; `null` si es nuevo. */
  frecuencia: number | null;
  ejemplo: string | null;
  plantillas: { id: number; nombre: string }[];
  grupos: string[];
  subgrupos: string[];
  fuentesConocidas: string[];
  /** El id del ítem guardado; `null` si es nuevo. */
  idDelItem: number | null;
  /** Los ítems compartidos del maestro: de ahí salen las dependencias posibles. */
  candidatos: { id: number; nombre: string }[];
  onCambiar: (cambios: Partial<EdicionDeItemDelMaestro>) => void;
  onCiclarUso: (plantillaId: number) => void;
  onCerrar: () => void;
}

/**
 * Cajón de un ítem del maestro. Acá SÍ se edita todo —es el lugar del
 * ítem—, y los cambios valen para todas las plantillas que lo usan. El uso por
 * plantilla se edita con un clic en su píldora: vacío → O → C → P → vacío, y
 * viaja con "Guardar cambios" como el resto (a diferencia del cajón de una
 * plantilla, que guarda el estado de las OTRAS en el acto).
 */
export function DrawerDeItemDelMaestro({
  edicion,
  frecuencia,
  ejemplo,
  plantillas,
  grupos,
  subgrupos,
  fuentesConocidas,
  idDelItem,
  candidatos,
  onCambiar,
  onCiclarUso,
  onCerrar,
}: DrawerProps) {
  const usadas = edicion ? Object.values(edicion.uso).filter(Boolean).length : 0;

  return (
    <Sheet open={edicion !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <SheetContent
        showCloseButton={false}
        // El nombre NO se enfoca al abrir: seleccionarlo invita a pisarlo sin querer.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="w-full gap-0 overflow-y-auto sm:max-w-[29.375rem]"
      >
        {edicion ? (
          <>
            <CabeceraDelCajon
              titulo={edicion.nombre || "Ítem sin nombre"}
              grupo={edicion.grupo}
              subgrupo={edicion.subgrupo || null}
            />

            <div className="flex flex-col gap-[1.125rem] px-5.5 pt-5 pb-7">
              <CampoDelCajon id="maestro-nombre" etiqueta="Ítem">
                <Input
                  id="maestro-nombre"
                  value={edicion.nombre}
                  onChange={(e) => onCambiar({ nombre: e.target.value })}
                />
              </CampoDelCajon>
              <CampoDelCajon id="maestro-descripcion" etiqueta="Descripción / contenido">
                <Textarea
                  id="maestro-descripcion"
                  rows={3}
                  value={edicion.descripcion}
                  onChange={(e) => onCambiar({ descripcion: e.target.value })}
                />
              </CampoDelCajon>

              <div className="grid grid-cols-2 gap-2.5">
                <CampoDelCajon id="maestro-grupo" etiqueta="Grupo">
                  <NativeSelect
                    id="maestro-grupo"
                    value={edicion.grupo}
                    onChange={(e) => onCambiar({ grupo: e.target.value, subgrupo: "" })}
                  >
                    {grupos.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </NativeSelect>
                </CampoDelCajon>
                <CampoDelCajon id="maestro-subgrupo" etiqueta="Subgrupo">
                  <NativeSelect
                    id="maestro-subgrupo"
                    value={edicion.subgrupo}
                    onChange={(e) => onCambiar({ subgrupo: e.target.value })}
                  >
                    <option value="">Sin subgrupo</option>
                    {subgrupos.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </NativeSelect>
                </CampoDelCajon>
                <CampoDelCajon id="maestro-tipo" etiqueta="Tipo de contenido">
                  <NativeSelect
                    id="maestro-tipo"
                    value={edicion.tipo}
                    onChange={(e) => onCambiar({ tipo: e.target.value as TipoDeContenido })}
                  >
                    {TIPOS_DE_CONTENIDO.map((t) => (
                      <option key={t.valor} value={t.valor}>
                        {t.etiqueta}
                      </option>
                    ))}
                  </NativeSelect>
                </CampoDelCajon>
                <CampoDelCajon id="maestro-fuente" etiqueta="Fuente de información">
                  <Input
                    id="maestro-fuente"
                    list="maestro-fuentes"
                    value={edicion.fuente}
                    onChange={(e) => onCambiar({ fuente: e.target.value })}
                  />
                  <datalist id="maestro-fuentes">
                    {fuentesConocidas.map((f) => (
                      <option key={f} value={f} />
                    ))}
                  </datalist>
                </CampoDelCajon>
              </div>

              <SeccionGeneracionConIa
                idDelItem={idDelItem}
                tipo={edicion.tipo}
                nombre={edicion.nombre}
                produccion={edicion}
                candidatos={candidatos}
                onCambiar={onCambiar}
              />

              <CampoDelCajon
                id="maestro-condicion"
                etiqueta="Condición sugerida"
                nota="cuando es condicional"
              >
                <Input
                  id="maestro-condicion"
                  value={edicion.condicionSugerida}
                  onChange={(e) => onCambiar({ condicionSugerida: e.target.value })}
                  placeholder="Ej: Si tiene construcciones"
                />
              </CampoDelCajon>

              <UsoPorPlantilla
                titulo={`Plantillas que lo usan · ${usadas} de ${plantillas.length}`}
                usos={plantillas.map((p) => ({
                  plantillaId: p.id,
                  nombre: p.nombre,
                  obligatoriedad: edicion.uso[String(p.id)] ?? "",
                }))}
                onCiclar={onCiclarUso}
              />

              {frecuencia !== null ? (
                <section className="flex flex-col gap-2">
                  <RotuloDeSeccion tono="hint">Presencia en informes analizados</RotuloDeSeccion>
                  <BarraDePresencia valor={frecuencia} total={INFORMES_ANALIZADOS} />
                  <p className="text-ink-muted text-xs leading-[1.45] font-medium">
                    Ejemplo: {ejemplo || "—"}
                  </p>
                </section>
              ) : null}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
